// ==================== SECURITY UTILITIES ====================
// Savidna CRM - Security layer for authentication and data protection

// ==================== PASSWORD HASHING (SHA-256 + Salt) ====================
// Note: For production, use Supabase Auth or bcrypt via Edge Functions.
// This is a significant improvement over plaintext storage.

const SALT_PREFIX = 'savidna_v1_'

async function sha256(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

export async function hashPassword(password: string): Promise<string> {
  // Double hash with salt for extra security
  const salted = SALT_PREFIX + password
  const firstHash = await sha256(salted)
  const secondHash = await sha256(firstHash + SALT_PREFIX)
  return secondHash
}

export async function verifyPassword(
  password: string,
  storedHash: string,
): Promise<boolean> {
  const hash = await hashPassword(password)
  return hash === storedHash
}

// Check if a stored password is already hashed (64 hex chars = SHA-256)
export function isPasswordHashed(password: string): boolean {
  if (!password || typeof password !== 'string') return false
  return /^[a-f0-9]{64}$/.test(password)
}

// ==================== RATE LIMITING ====================

interface RateLimitEntry {
  attempts: number
  firstAttempt: number
  lockedUntil: number | null
}

const RATE_LIMIT_KEY = 'savidna-rate-limit'
const MAX_ATTEMPTS = 5
const LOCK_DURATION_MS = 5 * 60 * 1000 // 5 minutes
const WINDOW_MS = 15 * 60 * 1000 // 15 minute window

function getRateLimitEntry(): RateLimitEntry {
  try {
    const saved = localStorage.getItem(RATE_LIMIT_KEY)
    if (saved) return JSON.parse(saved)
  } catch {}
  return { attempts: 0, firstAttempt: 0, lockedUntil: null }
}

function saveRateLimitEntry(entry: RateLimitEntry) {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(entry))
}

export function checkRateLimit(): {
  allowed: boolean
  remainingAttempts: number
  lockedUntilMs: number | null
  message?: string
} {
  const entry = getRateLimitEntry()
  const now = Date.now()

  // Check if currently locked
  if (entry.lockedUntil && now < entry.lockedUntil) {
    const remainingSeconds = Math.ceil((entry.lockedUntil - now) / 1000)
    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60
    return {
      allowed: false,
      remainingAttempts: 0,
      lockedUntilMs: entry.lockedUntil,
      message: `Demasiados intentos fallidos. Intenta de nuevo en ${minutes}:${seconds.toString().padStart(2, '0')}`,
    }
  }

  // Reset if window expired
  if (entry.firstAttempt && now - entry.firstAttempt > WINDOW_MS) {
    saveRateLimitEntry({ attempts: 0, firstAttempt: 0, lockedUntil: null })
    return {
      allowed: true,
      remainingAttempts: MAX_ATTEMPTS,
      lockedUntilMs: null,
    }
  }

  const remaining = MAX_ATTEMPTS - entry.attempts
  return {
    allowed: remaining > 0,
    remainingAttempts: Math.max(0, remaining),
    lockedUntilMs: null,
  }
}

export function recordFailedAttempt(): {
  locked: boolean
  remainingAttempts: number
  message?: string
} {
  const entry = getRateLimitEntry()
  const now = Date.now()

  // Reset if window expired
  if (entry.firstAttempt && now - entry.firstAttempt > WINDOW_MS) {
    entry.attempts = 0
    entry.firstAttempt = 0
    entry.lockedUntil = null
  }

  entry.attempts += 1
  if (!entry.firstAttempt) entry.firstAttempt = now

  if (entry.attempts >= MAX_ATTEMPTS) {
    entry.lockedUntil = now + LOCK_DURATION_MS
    saveRateLimitEntry(entry)
    return {
      locked: true,
      remainingAttempts: 0,
      message:
        'Cuenta bloqueada temporalmente por demasiados intentos fallidos. Espera 5 minutos.',
    }
  }

  saveRateLimitEntry(entry)
  const remaining = MAX_ATTEMPTS - entry.attempts
  return {
    locked: false,
    remainingAttempts: remaining,
    message: `Credenciales incorrectas. ${remaining} intento(s) restante(s).`,
  }
}

export function resetRateLimit() {
  localStorage.removeItem(RATE_LIMIT_KEY)
}

// ==================== SESSION MANAGEMENT ====================

interface SessionData {
  userId: string
  createdAt: number
  expiresAt: number
  token: string
}

const SESSION_KEY = 'savidna-session'
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000 // 8 hours

function generateToken(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function createSession(userId: string): SessionData {
  const now = Date.now()
  const session: SessionData = {
    userId,
    createdAt: now,
    expiresAt: now + SESSION_DURATION_MS,
    token: generateToken(),
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  return session
}

export function getValidSession(): SessionData | null {
  try {
    const saved = localStorage.getItem(SESSION_KEY)
    if (!saved) return null

    const session: SessionData = JSON.parse(saved)
    const now = Date.now()

    // Check expiration
    if (now > session.expiresAt) {
      destroySession()
      return null
    }

    return session
  } catch {
    destroySession()
    return null
  }
}

export function destroySession() {
  localStorage.removeItem(SESSION_KEY)
  localStorage.removeItem('savidna-user-id') // Clean legacy key too
}

// ==================== INPUT SANITIZATION ====================

export function sanitizeInput(input: string): string {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim()
}

export function sanitizeForDisplay(input: string): string {
  // For displaying user-generated content safely
  const div = document.createElement('div')
  div.textContent = input
  return div.innerHTML
}

// ==================== SECURITY HEADERS / LOGGING ====================

export function logSecurityEvent(
  event:
    | 'login_success'
    | 'login_failed'
    | 'logout'
    | 'session_expired'
    | 'rate_limited'
    | 'data_export'
    | 'password_changed',
  details?: string,
) {
  const timestamp = new Date().toISOString()
  // In production, send to server-side logging
  console.log(
    `🔒 [SECURITY] ${timestamp} - ${event}${details ? ': ' + details : ''}`,
  )
}
